import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  PHONE_COUNTRIES, PHONE_DEFAULT_ISO2, flagEmoji, phoneDigits, findPhoneCountry,
  parsePhone, composePhone, isValidNational, phoneCountryMatches,
} from '../../utils/phonePolicy.js';
import {
  groupNationalDigits, examplePhoneNational, stripTrunkPrefix, maxNationalDigits, caretAfterDigits,
} from '../../utils/phoneGroups.js';

/**
 * Ô nhập SỐ ĐIỆN THOẠI có bộ chọn mã vùng quốc gia (cờ + mã) đứng trước.
 *
 * Vì sao có: trước 29/08/2026 mọi ô SĐT trong hệ chỉ nhận số Việt Nam, khách Canada
 * nhập `+1 226…` là bị "Số điện thoại không đúng định dạng" mà không có cách nào chữa.
 *
 * Luật số (mã quốc gia, độ dài, quy ước lưu) nằm ở kernel `utils/phonePolicy.js` — file
 * SINH RA từ `cnv-server-shared`, cùng luật với 3 backend. Đừng chép luật vào đây.
 *
 * Từ 23/09/2026 ô tự NHÓM số theo nước đang chọn ngay lúc gõ — 🇨🇦 `(226) 503-5441`,
 * 🇻🇳 `909 123 456` — và bỏ tiền tố gọi trong nước gõ thừa (`0` của VN, `1` của Bắc Mỹ).
 * Chỉ là cách HIỂN THỊ (`utils/phoneGroups.js`): `onChange` vẫn phát chuỗi chữ số trần theo
 * quy ước lưu, như trước.
 *
 * Props:
 *  - value        chuỗi đầy đủ, vd '0901234567' (VN) hoặc '+12265035441' (nước khác)
 *  - onChange     (value: string) => void — trả về chuỗi đầy đủ theo quy ước lưu
 *  - defaultCountry ISO2 dùng khi value trống; bỏ trống thì đoán theo ngôn ngữ trình duyệt
 *  - pinned       ISO2 xếp lên đầu danh sách khi chưa gõ tìm kiếm
 *  - invalid      viền đỏ (câu lỗi do chỗ gọi tự hiển thị)
 *  - disabled / placeholder / id / name / autoFocus / onBlur / className
 */
export default function PhoneInput({
  value = '',
  onChange,
  defaultCountry,
  pinned = ['VN', 'US', 'CA', 'GB', 'AU', 'SG'],
  invalid = false,
  disabled = false,
  placeholder,
  id,
  name,
  autoFocus = false,
  onBlur,
  className = '',
}) {
  const { t, i18n } = useLingui();

  // Nước mặc định: prop → vùng của ngôn ngữ trình duyệt (en-CA → CA) → VN.
  const fallbackIso2 = useMemo(() => {
    if (findPhoneCountry(defaultCountry)) return String(defaultCountry).toUpperCase();
    try {
      const region = new Intl.Locale(navigator.language).region;
      if (findPhoneCountry(region)) return region;
    } catch { /* trình duyệt cũ không có Intl.Locale — bỏ qua */ }
    return PHONE_DEFAULT_ISO2;
  }, [defaultCountry]);

  const [iso2, setIso2] = useState(() => parsePhone(value, fallbackIso2).iso2);
  const [national, setNational] = useState(() => parsePhone(value, fallbackIso2).national);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  // Số chữ số đứng TRƯỚC con trỏ sau lượt gõ vừa rồi — ô tự chèn ngoặc/dấu cách nên vị trí
  // con trỏ theo ký tự đổi sau mỗi lần render; đếm theo chữ số thì không.
  const caretDigitsRef = useRef(null);

  // Đồng bộ khi value đổi TỪ NGOÀI (mở modal của nhân viên khác, reset form…).
  // So bằng chuỗi mình sẽ phát ra để không tự đá vào chính mình lúc người dùng đang gõ.
  useEffect(() => {
    if (String(value || '') === composePhone(iso2, national)) return;
    const p = parsePhone(value, fallbackIso2);
    setIso2(p.iso2);
    setNational(p.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, fallbackIso2]);

  const country = findPhoneCountry(iso2) || findPhoneCountry(fallbackIso2);

  const emit = (nextIso2, nextNational) => {
    setIso2(nextIso2);
    setNational(nextNational);
    onChange?.(composePhone(nextIso2, nextNational));
  };

  const display = groupNationalDigits(country, national);

  const placeCaret = () => {
    const n = caretDigitsRef.current;
    caretDigitsRef.current = null;
    const el = inputRef.current;
    if (n == null || !el || document.activeElement !== el) return;
    const pos = caretAfterDigits(el.value, n);
    el.setSelectionRange(pos, pos);
  };
  // Chạy sau MỖI lần render nhưng chỉ làm việc khi vừa có lượt gõ (ref khác null).
  useLayoutEffect(placeCaret);

  const handleTyping = (e) => {
    const el = e.target;
    const raw = el.value;
    // Dán nguyên số quốc tế vào ô số ('+1 226…' / '0044…') → tự nhảy đúng cờ.
    if (/^\s*(\+|00)/.test(raw)) {
      const p = parsePhone(raw, iso2);
      if (p.national) {
        caretDigitsRef.current = Infinity;
        emit(p.iso2, p.national);
        return;
      }
    }
    let digits = phoneDigits(raw);
    let before = phoneDigits(raw.slice(0, el.selectionStart ?? raw.length)).length;

    // Xoá đúng vào dấu do ô tự chèn — `(`, `)`, `-`, dấu cách: chữ số không đổi nên ô vẽ lại y
    // hệt, người dùng bấm xoá hoài mà con trỏ đứng im. Xoá luôn chữ số liền kề theo chiều đang xoá.
    const kind = e.nativeEvent?.inputType || '';
    if (kind.startsWith('delete') && digits === national && raw.length < display.length) {
      if (kind === 'deleteContentForward') {
        digits = digits.slice(0, before) + digits.slice(before + 1);
      } else if (before > 0) {
        digits = digits.slice(0, before - 1) + digits.slice(before);
        before -= 1;
      }
    }

    const bare = stripTrunkPrefix(country, digits);
    before = Math.max(0, before - (digits.length - bare.length));
    const next = bare.slice(0, maxNationalDigits(country));
    caretDigitsRef.current = Math.min(before, next.length);
    if (next === national) {
      // Không đổi gì (gõ chữ cái, gõ quá độ dài) ⇒ React không render lại, ô tự trả về giá trị
      // cũ và con trỏ nhảy về cuối. Đặt lại con trỏ ở khung hình kế tiếp.
      requestAnimationFrame(placeCaret);
      return;
    }
    emit(iso2, next);
  };

  const pickCountry = (nextIso2) => {
    const nextCountry = findPhoneCountry(nextIso2);
    emit(nextIso2, stripTrunkPrefix(nextCountry, national).slice(0, maxNationalDigits(nextCountry)));
    setOpen(false);
    setSearch('');
    inputRef.current?.focus();
  };

  // Định vị dropdown (portal, position fixed) — ô này hay nằm trong modal có overflow.
  useEffect(() => {
    if (!open) return undefined;
    const updatePos = () => {
      const r = boxRef.current?.getBoundingClientRect();
      if (!r) return;
      const dropdownH = 320;
      const dropdownW = Math.max(r.width, 300);
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const placeAbove = spaceBelow < dropdownH && spaceAbove > spaceBelow;
      const maxLeft = window.innerWidth - dropdownW - 8;
      setPos({
        top: placeAbove ? Math.max(8, r.top - dropdownH - 4) : r.bottom + 4,
        left: Math.max(8, Math.min(r.left, maxLeft)),
        width: dropdownW,
        maxH: placeAbove ? Math.min(dropdownH, spaceAbove - 8) : Math.min(dropdownH, spaceBelow - 8),
      });
    };
    updatePos();
    // Tự đặt con trỏ vào ô tìm — KHÔNG dùng autoFocus được: lúc mount dropdown còn
    // `visibility:hidden` (đợi tính xong toạ độ) nên trình duyệt bỏ qua autofocus, người
    // dùng gõ mà không chữ nào vào ô.
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target) && !e.target.closest('[data-phone-dropdown]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  // Tên nước theo ĐÚNG ngôn ngữ đang dùng — không phải dịch tay 242 cái tên.
  const regionNames = useMemo(() => {
    try { return new Intl.DisplayNames([i18n.locale || 'vi'], { type: 'region' }); } catch { return null; }
  }, [i18n.locale]);
  const nameOf = (c) => {
    try { return regionNames?.of(c.iso2) || c.name; } catch { return c.name; }
  };

  const list = useMemo(() => {
    const named = PHONE_COUNTRIES.map((c) => ({ ...c, label: nameOf(c) }));
    const hits = named.filter((c) => phoneCountryMatches(c, search, c.label));
    hits.sort((a, b) => a.label.localeCompare(b.label, i18n.locale || 'vi'));
    if (search.trim()) return hits;
    const top = pinned
      .map((iso) => hits.find((c) => c.iso2 === iso))
      .filter(Boolean);
    const topSet = new Set(top.map((c) => c.iso2));
    return [...top, ...hits.filter((c) => !topSet.has(c.iso2))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, i18n.locale, regionNames, pinned]);

  const pinnedCount = search.trim() ? 0 : pinned.filter((iso) => findPhoneCountry(iso)).length;
  const filled = phoneDigits(national).length > 0;
  const looksWrong = invalid || (filled && !isValidNational(country, national));

  return (
    <div className={`w-full min-w-0 ${className}`}>
      <div
        ref={boxRef}
        className={`flex items-stretch w-full min-w-0 rounded-xl border bg-white overflow-hidden transition
          ${looksWrong ? 'border-red-400 focus-within:ring-1 focus-within:ring-red-400' : 'border-gray-300 focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500'}
          ${disabled ? 'bg-gray-50' : ''}`}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          aria-label={t`Chọn mã vùng quốc gia`}
          title={`${nameOf(country)} (+${country.dial})`}
          className={`flex items-center gap-1 shrink-0 pl-3 pr-2 border-r border-gray-200 text-sm text-gray-700
            ${disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
        >
          <span className="text-base leading-none">{flagEmoji(country.iso2)}</span>
          <span className="tabular-nums">+{country.dial}</span>
          <ChevronDown size={13} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
        </button>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={disabled}
          value={display}
          onChange={handleTyping}
          onBlur={onBlur}
          placeholder={placeholder ?? examplePhoneNational(country) ?? t`Số điện thoại`}
          className="flex-1 min-w-0 px-3 py-2.5 text-sm outline-none bg-transparent disabled:cursor-not-allowed"
        />
      </div>

      {open && ReactDOM.createPortal(
        <div
          data-phone-dropdown
          className="fixed bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-[10010] flex flex-col"
          style={{
            top: pos.top, left: pos.left, width: pos.width || 300, maxHeight: pos.maxH || 320,
            visibility: pos.width ? 'visible' : 'hidden',
          }}
        >
          <div className="p-2 border-b border-gray-100 shrink-0">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                  if (e.key === 'Enter' && list[0]) { e.preventDefault(); pickCountry(list[0].iso2); }
                }}
                className="flex-1 min-w-0 text-sm bg-transparent outline-none"
                placeholder={t`Tìm nước hoặc mã vùng (vd 226, +1, Canada)`}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {list.map((c, idx) => (
              <div key={c.iso2}>
                {pinnedCount > 0 && idx === pinnedCount && <div className="border-t border-gray-100 my-1" />}
                <button
                  type="button"
                  onClick={() => pickCountry(c.iso2)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-blue-50 transition
                    ${c.iso2 === country.iso2 ? 'bg-blue-50/60 font-medium' : ''}`}
                >
                  <span className="text-base leading-none shrink-0">{flagEmoji(c.iso2)}</span>
                  <span className="flex-1 min-w-0 truncate text-gray-700">{c.label}</span>
                  <span className="shrink-0 text-gray-500 tabular-nums">+{c.dial}</span>
                </button>
              </div>
            ))}
            {list.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">
                <Trans>Không tìm thấy nước nào</Trans>
              </p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export { PhoneInput };
